import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCreateOrganizationPartnerMutation } from '@client/graphql/__generated__/main-operations';
import { PartnerType } from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import { memo, useCallback, useState } from 'react';
import { useAppContext } from '~/services/main/contexts/AppContext';

interface AddPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PartnerTypeOption {
  label: string;
  value: PartnerType;
}

const partnerTypeOptions: PartnerTypeOption[] = [
  { label: 'Lender', value: PartnerType.LENDER },
  { label: 'Title', value: PartnerType.TITLE },
  { label: 'Insurance', value: PartnerType.INSURANCE },
];

const selectStyles = {
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    fontSize: 'md',
    py: 2,
    px: 3,
    cursor: 'pointer',
    _hover: { bg: 'whiteAlpha.100' },
  }),
};

export const AddPartnerModal = memo(function AddPartnerModal({
  isOpen,
  onClose,
  onSuccess,
}: AddPartnerModalProps) {
  const { selectedOrganization } = useAppContext();
  const toast = useToast();

  const [partnerName, setPartnerName] = useState('');
  const [partnerType, setPartnerType] = useState<PartnerTypeOption | null>(
    null
  );

  const [createPartner, { loading }] = useCreateOrganizationPartnerMutation();

  const resetForm = useCallback(() => {
    setPartnerName('');
    setPartnerType(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!selectedOrganization?.id) {
      toast({
        title: 'Error',
        description: 'No organization selected',
        status: 'error',
        duration: 3000,
      });

      return;
    }

    if (!partnerName.trim() || !partnerType) {
      toast({
        title: 'Missing fields',
        description: 'Partner name and type are required',
        status: 'warning',
        duration: 3000,
      });

      return;
    }

    try {
      await createPartner({
        variables: {
          input: {
            organizationId: selectedOrganization.id,
            name: partnerName.trim(),
            partnerType: partnerType.value,
          },
        },
      });

      toast({
        title: 'Partner added',
        description: `${partnerName} has been added as a partner`,
        status: 'success',
        duration: 3000,
      });

      handleClose();
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to add partner',
        status: 'error',
        duration: 5000,
      });
    }
  }, [
    selectedOrganization?.id,
    partnerName,
    partnerType,
    createPartner,
    toast,
    handleClose,
    onSuccess,
  ]);

  const isValid = partnerName.trim() && partnerType;

  return (
    <Modal isOpen={isOpen} size="md" onClose={handleClose}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="indigo.700" borderRadius="xl" mx={4}>
        <ModalHeader
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
          fontSize="xs"
          fontWeight="medium"
          letterSpacing="0.88px"
          pb={4}
          pt={5}
          textTransform="uppercase"
        >
          New Partner
        </ModalHeader>
        <ModalCloseButton color="whiteAlpha.600" top={4} />

        <ModalBody pb={6} pt={5}>
          <VStack spacing={5}>
            {/* Partner Name */}
            <FormControl
              borderBottom="1px solid"
              borderColor="whiteAlpha.100"
              pb={5}
            >
              <FormLabel color="whiteAlpha.700">Partner name</FormLabel>
              <Input
                placeholder="Enter partner name"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
              />
            </FormControl>

            {/* Partner Type */}
            <FormControl
              borderBottom="1px solid"
              borderColor="whiteAlpha.100"
              pb={5}
            >
              <FormLabel color="whiteAlpha.700">Partner type</FormLabel>
              <Select<PartnerTypeOption>
                chakraStyles={selectStyles}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                isSearchable={false}
                menuPlacement="auto"
                options={partnerTypeOptions}
                placeholder="Please select"
                value={partnerType}
                onChange={(newValue) => {
                  setPartnerType(newValue);
                }}
              />
            </FormControl>

            {/* Submit Button */}
            <Button
              _hover={{ bg: 'cyan.600' }}
              bg="cyan.500"
              color="indigo.900"
              fontWeight="medium"
              isDisabled={!isValid}
              isLoading={loading}
              size="lg"
              width="100%"
              onClick={() => void handleSubmit()}
            >
              Create
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});
